import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  afterNextRender,
  signal,
} from '@angular/core';

/** A single navigation link in the footer. */
export interface FooterLink {
  /** Text displayed in the button. */
  label: string;
  /** Identifier emitted when the button is clicked. */
  id: string;
}

/**
 * Footer — bottom bar from the ComPsych Design System.
 *
 * Displays a copyright notice and an optional list of navigation links.
 * On initialisation the component measures the natural widths of the copyright
 * and links to establish a breakpoint. On every window resize it compares the
 * host width against that breakpoint and switches between a row layout
 * (copyright left, links right) and a stacked, centered column layout.
 */
@Component({
  selector: 'compsych-footer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './footer.html',
  styleUrl: './footer.css',
  host: {
    role: 'contentinfo',
    '[class.compsych-footer--row]': 'isRow()',
  },
})
export class FooterComponent implements OnChanges, OnDestroy {
  /**
   * Navigation links rendered alongside the copyright notice.
   * The layout switches automatically based on their combined natural width.
   */
  @Input() links: FooterLink[] = [];

  /** Copyright text displayed in the footer. */
  @Input() copyright = '';

  /** Emits the id of the link button that was clicked. */
  @Output() linkClick = new EventEmitter<string>();

  /**
   * Reflects whether the row layout is active.
   * Signal so OnPush picks up DOM-driven updates from async callbacks.
   */
  protected readonly isRow = signal(false);

  /** Combined natural width of copyright + links + gap + padding. */
  private breakpoint = 0;

  private readonly widthObserver = new ResizeObserver(() => {
    requestAnimationFrame(() => {
      const items = Array.from(
        this.el.nativeElement.querySelectorAll<HTMLElement>('.compsych-footer__links li'),
      );
      const dividers = Array.from(
        this.el.nativeElement.querySelectorAll<HTMLElement>('.compsych-footer__divider'),
      );
      dividers.forEach((d) => (d.style.display = ''));
      let prevTop: number | null = null;
      items.forEach((item, i) => {
        const top = item.offsetTop;
        if (prevTop !== null && top > prevTop) {
          dividers[i - 1].style.display = 'none';
        }
        prevTop = top;
      });
    });
  });

  constructor(private readonly el: ElementRef<HTMLElement>) {
    afterNextRender(() => {
      this.widthObserver.observe(this.el.nativeElement);
      this.measureBreakpoint();
      this.checkLayout();
    });
  }

  ngOnDestroy(): void {
    this.widthObserver.disconnect();
  }

  ngOnChanges(): void {
    requestAnimationFrame(() => {
      this.measureBreakpoint();
      this.checkLayout();
    });
  }

  @HostListener('window:resize')
  protected onResize(): void {
    this.checkLayout();
  }

  /**
   * Measures the natural widths of the copyright and links elements once and
   * stores their sum (plus padding and gap) as the layout breakpoint.
   */
  private measureBreakpoint(): void {
    const host = this.el.nativeElement;
    const copyrightEl = host.querySelector<HTMLElement>('.compsych-footer__copyright');
    const linksEl = host.querySelector<HTMLElement>('.compsych-footer__links');
    if (!copyrightEl) return;

    const style = getComputedStyle(host);
    const paddingInline = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const gap = parseFloat(style.columnGap) || 0;

    this.breakpoint =
      copyrightEl.scrollWidth +
      (linksEl ? linksEl.scrollWidth + gap : 0) +
      paddingInline;
  }

  /** Switches to the row layout when the host is wide enough for both children. */
  private checkLayout(): void {
    if (!this.breakpoint) return;
    this.isRow.set(this.el.nativeElement.offsetWidth >= this.breakpoint);
  }

}
